import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection/AccordionSection";
import ManagedAccordionSection from "@insite/mobius/AccordionSection/ManagedAccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadWarehouse from "@WhiteCap/Handlers/WarehouseHandler/LoadWarehouse";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

type WarehouseDetails = {
    description: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    postalCode: string;
    phone: string;
};

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{
        orderId?: string;
    }>(location.search);
    const orderNumber = parsedQuery.orderId ?? parsedQuery.orderId;

    return {
        orderData: orderNumber ? state.WhiteCap.customOrderStatusPage.orderStatusById[orderNumber] : null,
        pageType: getCurrentPage(state).type,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface OrderPoStyles {
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    orderDelivery?: TypographyPresentationProps;
    section?: InjectableCss;
    subSection?: InjectableCss;
    sectionTitle?: TypographyProps;
    subTitle?: TypographyProps;
    info?: TypographyProps;
    infoBold?: TypographyProps;
    centeringWrapper?: InjectableCss;
    phoneLink?: LinkPresentationProps;
    managedAccordion?: AccordionSectionPresentationProps;
    accountManagerLinksWrapper?: InjectableCss;
    accordionHeading?: TypographyProps;
}

export const additionalOrderDetailsStyles: OrderPoStyles = {
    wrapper: {
        css: css`
            padding-bottom: 10px;
            text-align: center;
        `,
    },
    contentWrapper: {
        css: css`
            display: grid;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            grid-template-columns: repeat(1, 1fr);
                            gap: 12px;
                        `,
                        css``,
                        css`
                            grid-template-columns: repeat(2, 1fr);
                            gap: 24px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    section: {
        css: css`
            background: ${getColor("common.accent")};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 16px;
                        `,
                        css``,
                        css`
                            padding: 16px 24px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subSection: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    sectionTitle: {
        weight: 500,
        size: 16,
        css: css`
            display: block;
            text-align: left;
            margin-bottom: 8px;
        `,
    },
    info: {
        size: 16,
        css: css`
            display: block;
            text-align: left;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 14px;
                        `,
                        css``,
                        css`
                            font-size: 16px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    infoBold: {
        size: 16,
        weight: 450,
        css: css`
            display: block;
            text-align: left;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 14px;
                        `,
                        css``,
                        css`
                            font-size: 16px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subTitle: {
        weight: 500,
        size: 16,
        css: css`
            display: block;
            text-align: left;
            margin-top: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 14px;
                        `,
                        css``,
                        css`
                            font-size: 16px;
                            margin-bottom: 8px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    centeringWrapper: {
        css: css`
            height: 150px;
            display: flex;
            align-items: center;
        `,
    },
    phoneLink: {
        typographyProps: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 14px;
                            `,
                            css``,
                            css`
                                font-size: 16px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        css: css`
            text-decoration: none;
            text-align: left;
            display: block;
        `,
    },
    managedAccordion: {
        panelProps: {
            css: css`
                padding: 24px 0;
                border: none;
            `,
        },
        headerProps: {
            css: css`
                > button {
                    background: #858585;
                    border: none;
                    color: #ffffff;
                    padding: 12px 24px;
                    &:focus {
                        border: none;
                        padding: 12px 24px;
                    }
                }
            `,
        },
        titleTypographyProps: {
            color: "#fffff",
            variant: "h3",
            css: css`
                margin-bottom: 0;
            `,
        },
        toggleIconProps: {
            src: ChevronDown,
            css: css`
                color: #ffffff;
            `,
        },
    },
    accountManagerLinksWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap:
        `,
    },
    accordionHeading: {
        css: css`
            margin-bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                        `,
                        null,
                        css`
                            font-size: 20px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = additionalOrderDetailsStyles;

const formatPhone = (input: string): string => {
    // input: 1234567890
    // Output: (123) 456-7890
    if (input.length < 10) {
        return input;
    }

    const areaCode = input.slice(0, 3);
    const part1 = input.slice(3, 5);
    const part2 = input.slice(5);

    return `(${areaCode}) ${part1}-${part2}`;
};

const AdditionalOrderDetails: FC<Props> = ({ pageType, orderData, fields }) => {
    const order = pageType === "CustomOrderStatusPage" ? orderData : useContext(OrderStateContext).value;

    if (!order || !order?.orderLines) {
        return null;
    }

    const warehouseDetails = order.properties.warehouseDetails
        ? (JSON.parse(order.properties.warehouseDetails) as WarehouseDetails)
        : null;
    const accordionTitleArr = fields.title.split(" ");
    const accordionHeading = accordionTitleArr.map((word, i) => {
        return (
            <Typography key={i} {...styles.accordionHeading}>
                {word === "#{{orderNumber}}" ? (
                    <b>{word.replace("#{{orderNumber}}", `#${order.erpOrderNumber}`)}</b>
                ) : (
                    word
                )}{" "}
            </Typography>
        );
    });
    return (
        <StyledWrapper {...styles.wrapper}>
            <Accordion headingLevel={1}>
                <ManagedAccordionSection
                    title={<StyledWrapper>{accordionHeading}</StyledWrapper>}
                    initialExpanded={true}
                    {...styles.managedAccordion}
                >
                    <StyledWrapper {...styles.contentWrapper}>
                        <StyledWrapper {...styles.section}>
                            <Typography {...styles.sectionTitle}>Account</Typography>
                            <Typography {...styles.info}>
                                #{order.customerNumber}
                                {order.properties?.accountInfo ? ` | ${order.properties.accountInfo}` : ""}
                            </Typography>
                            {!order.properties?.salesRepName &&
                            !order.properties?.salesRepEmail &&
                            !order.properties?.salesRepMobile ? (
                                ""
                            ) : (
                                <>
                                    <Typography {...styles.subTitle}>Account Manager</Typography>
                                    <Typography {...styles.info}>{order.properties?.salesRepName}</Typography>
                                    <StyledWrapper {...styles.accountManagerLinksWrapper}>
                                        <Link href={`tel:${order.properties.salesRepMobile}`} {...styles.phoneLink}>
                                            {order.properties?.salesRepMobile}
                                        </Link>
                                        {order.properties?.salesRepEmail && (
                                            <>
                                                &nbsp;&nbsp;|&nbsp;&nbsp;
                                                <Link
                                                    href={`mailto:${order.properties.salesRepEmail}`}
                                                    {...styles.phoneLink}
                                                >
                                                    {order.properties?.salesRepEmail}
                                                </Link>
                                            </>
                                        )}
                                    </StyledWrapper>
                                </>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.section}>
                            <Typography {...styles.sectionTitle}>Deliver to jobsite/address</Typography>
                            <Typography {...styles.infoBold}>{order.stCompanyName}</Typography>
                            <Typography {...styles.info}>
                                {order.stAddress1} {order.stAddress2} {order.stAddress3}
                            </Typography>
                            <Typography {...styles.info}>
                                {order.shipToCity}{" "}
                                {order.shipToCity && (order.shipToState || order.shipToPostalCode) ? "," : ""}{" "}
                                {order.shipToState} {order.shipToPostalCode}
                            </Typography>
                            <Typography {...styles.subTitle}>Jobsite Contact</Typography>
                            <Typography {...styles.info}>{order.properties?.jobSiteContact}</Typography>
                            {order.properties?.jobSitePhone && (
                                <Link href={`tel:${order.properties.jobSitePhone}`} {...styles.phoneLink}>
                                    {order.properties?.jobSitePhone}
                                </Link>
                            )}
                        </StyledWrapper>
                        {warehouseDetails && (
                            <StyledWrapper {...styles.section}>
                                <Typography {...styles.sectionTitle}>Ordered from branch</Typography>
                                <Typography {...styles.infoBold}>{warehouseDetails?.description}</Typography>
                                <Typography {...styles.info}>
                                    {warehouseDetails?.address1} {warehouseDetails?.address2}
                                </Typography>
                                <Typography {...styles.info}>
                                    {warehouseDetails?.city} {warehouseDetails?.state} {warehouseDetails?.postalCode}
                                </Typography>
                                {warehouseDetails?.phone && (
                                    <Link href={`tel:${warehouseDetails.phone}`} {...styles.phoneLink}>
                                        {warehouseDetails.phone}
                                    </Link>
                                )}
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.section}>
                            <StyledWrapper {...styles.subSection}>
                                <Typography {...styles.infoBold}>Ordered by</Typography>
                                <Typography {...styles.info}>{order.properties?.orderedBy}</Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.subSection}>
                                <Typography {...styles.infoBold}>Order created by</Typography>
                                <Typography {...styles.info}>{order.properties?.orderCreatedBy}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </ManagedAccordionSection>
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(AdditionalOrderDetails),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage", "CustomOrderStatusPage"],
        group: "Order Details",
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "General",
                defaultValue: "Order #{{orderData}} - Additional Information",
                editorTemplate: "TextField",
            },
        ],
    },
};

export default widgetModule;
